import type { ArrowDefinition, LevelDefinition } from '../game/model/types';
import {
  generationEffortLimit,
  largeBoardGenerationPasses,
  largeRectangleHamiltonianPartitionThreshold,
  rectangleMaskIds,
} from './curve';
import { computeInitialInterestMetrics, computeMetrics } from './difficulty';
import {
  orientPaths,
  prepareWardenPaths,
  type OrientedArrows,
} from './orient';
import {
  partitionMaskWithMetadata,
  type PartitionEffort,
  type SnakePath,
} from './partition';
import { createRng, deriveSeed, fnv1a32Hex, type Rng } from './rng';
import type { GenerationInput, GenerationResult } from './types';
import { carveVacancies } from './vacancy';

function interestRank(candidate: OrientedArrows, input: GenerationInput): {
  readonly targetCount: number;
  readonly score: number;
} {
  const params = input.params.chainTrap;
  if (params === undefined) return { targetCount: 0, score: 0 };
  const metrics = computeInitialInterestMetrics({
    id: input.levelId,
    title: input.title,
    width: input.mask.width,
    height: input.mask.height,
    arrows: candidate.arrows,
  });
  const initialRate = (candidate.initialRemovableCount ?? 0) / candidate.arrows.length;
  const trapInTarget = metrics.trapCount >= params.trapCountTarget[0]
    && metrics.trapCount <= params.trapCountTarget[1];
  const rateInTarget = initialRate >= params.initialRemovableRate[0]
    && initialRate <= params.initialRemovableRate[1];
  return {
    targetCount: Number(metrics.chainRunCount >= params.chainRunTarget)
      + Number(trapInTarget)
      + Number(metrics.trapCount <= params.maximumTrapCount)
      + Number(rateInTarget),
    score: Math.min(metrics.chainRunCount, params.chainRunTarget)
      + (params.trapCountTarget[0] === 0
        ? Number(metrics.trapCount <= params.trapCountTarget[1])
        : Math.min(metrics.trapCount, params.trapCountTarget[0])
          / params.trapCountTarget[0])
      - Math.max(0, metrics.trapCount - params.trapCountTarget[1]),
  };
}

export function vacancyProtectedArrowIndexes(
  oriented: Pick<
    OrientedArrows,
    'wardenArrowIndexes' | 'edgeSentinelArrowIndexes' | 'interestArrowIndexes'
  >,
): Set<number> {
  return new Set([
    ...(oriented.wardenArrowIndexes ?? []),
    ...(oriented.edgeSentinelArrowIndexes ?? []),
    ...(oriented.interestArrowIndexes ?? []),
  ]);
}

function orientWithPasses(
  paths: readonly SnakePath[],
  input: GenerationInput,
  rng: Rng,
  authoredInterestTailPathCount: number,
): OrientedArrows | null {
  const passes = input.mask.cells.length <= largeRectangleHamiltonianPartitionThreshold
    ? input.params.orientationPasses
    : 1;
  const warden = input.mask.cells.length > largeRectangleHamiltonianPartitionThreshold
    ? input.params.warden
    : undefined;
  const chainTrap = input.params.chainTrap;
  if (warden !== undefined) {
    const preparedPaths = prepareWardenPaths(
      paths,
      input.mask.width,
      input.mask.height,
      rng,
      warden,
      authoredInterestTailPathCount,
    );
    let best: OrientedArrows | null = null;
    let bestInitialRate = Number.POSITIVE_INFINITY;
    let bestVictimCount = Number.NEGATIVE_INFINITY;
    let bestTargetCount = Number.NEGATIVE_INFINITY;
    let bestTargetScore = Number.NEGATIVE_INFINITY;
    let bestInterestTargetCount = Number.NEGATIVE_INFINITY;
    let bestInterestScore = Number.NEGATIVE_INFINITY;
    const candidateCount = Math.max(
      warden.orientationCandidates,
      chainTrap?.orientationCandidates ?? 1,
    );
    for (let pass = 0; pass < candidateCount; pass += 1) {
      const candidate = orientPaths(
        preparedPaths,
        input.mask.width,
        input.mask.height,
        rng,
        1,
        {
          wardenParams: warden,
          wardenPathsPrepared: true,
          chainTrapParams: chainTrap,
          authoredInterestTailPathCount,
          edgeParams: input.params.edge,
        },
      );
      if (candidate === null || candidate.initialRemovableCount === undefined) continue;
      const victimCount = candidate.wardenVictimPairs?.length ?? 0;
      const longBlockerRatio = candidate.longBlockerRatio ?? 0;
      const peelDepth = candidate.peelDepth ?? 0;
      const initialRate = candidate.initialRemovableCount / candidate.arrows.length;
      const targetCount = Number(longBlockerRatio >= warden.longBlockerRatioTarget)
        + Number(peelDepth >= warden.peelDepthTarget)
        + Number(initialRate <= warden.initialRemovableRateTarget);
      const targetScore = Math.min(1, longBlockerRatio / warden.longBlockerRatioTarget)
        + Math.min(1, peelDepth / warden.peelDepthTarget)
        + Math.min(1, warden.initialRemovableRateTarget / initialRate);
      const candidateInterest = interestRank(candidate, input);
      if (targetCount > bestTargetCount
        || (targetCount === bestTargetCount && targetScore > bestTargetScore)
        || (targetCount === bestTargetCount && targetScore === bestTargetScore
          && candidateInterest.targetCount > bestInterestTargetCount)
        || (targetCount === bestTargetCount && targetScore === bestTargetScore
          && candidateInterest.targetCount === bestInterestTargetCount
          && candidateInterest.score > bestInterestScore)
        || (targetCount === bestTargetCount && targetScore === bestTargetScore
          && candidateInterest.targetCount === bestInterestTargetCount
          && candidateInterest.score === bestInterestScore
          && initialRate < bestInitialRate)
        || (targetCount === bestTargetCount && targetScore === bestTargetScore
          && candidateInterest.targetCount === bestInterestTargetCount
          && candidateInterest.score === bestInterestScore
          && initialRate === bestInitialRate
          && victimCount > bestVictimCount)) {
        best = candidate;
        bestInitialRate = initialRate;
        bestVictimCount = victimCount;
        bestTargetCount = targetCount;
        bestTargetScore = targetScore;
        bestInterestTargetCount = candidateInterest.targetCount;
        bestInterestScore = candidateInterest.score;
      }
    }
    return best;
  }
  const interestPaths = paths;
  const candidateCount = Math.max(passes, chainTrap?.orientationCandidates ?? 1);
  if (candidateCount === 1) {
    return orientPaths(
      interestPaths,
      input.mask.width,
      input.mask.height,
      rng,
      0,
      { edgeParams: input.params.edge },
    );
  }
  let best: OrientedArrows | null = null;
  let bestInitialRate = Number.POSITIVE_INFINITY;
  let bestInterestTargetCount = Number.NEGATIVE_INFINITY;
  let bestInterestScore = Number.NEGATIVE_INFINITY;
  for (let pass = 0; pass < candidateCount; pass += 1) {
    const candidate = orientPaths(
      interestPaths,
      input.mask.width,
      input.mask.height,
      rng,
      true,
      {
        chainTrapParams: chainTrap,
        authoredInterestTailPathCount,
        edgeParams: input.params.edge,
      },
    );
    if (candidate === null) continue;
    if (candidate.initialRemovableCount === undefined) {
      throw new Error('Multi-pass orientation must report its initial removable count.');
    }
    const candidateInitialRate = candidate.initialRemovableCount / candidate.arrows.length;
    const candidateInterest = interestRank(candidate, input);
    if (candidateInterest.targetCount > bestInterestTargetCount
      || (candidateInterest.targetCount === bestInterestTargetCount
        && candidateInterest.score > bestInterestScore)
      || (candidateInterest.targetCount === bestInterestTargetCount
        && candidateInterest.score === bestInterestScore
        && candidateInitialRate < bestInitialRate)) {
      best = candidate;
      bestInitialRate = candidateInitialRate;
      bestInterestTargetCount = candidateInterest.targetCount;
      bestInterestScore = candidateInterest.score;
    }
  }
  return best;
}

export interface RetryOutcome {
  readonly result: GenerationResult;
  readonly resolvedSeed: number;
  readonly attemptCount: number;
  readonly effort: number;
}

export function generateLevel(
  input: GenerationInput,
  effort: PartitionEffort = { count: 0 },
  effortLimit = Number.POSITIVE_INFINITY,
): GenerationResult | null {
  const rng = createRng(input.seed);
  // Large boards need bounded whole-layout backtracking when a valid partition
  // cannot be oriented; legacy boards keep their exact single-pass behavior.
  const generationPasses = input.mask.cells.length > 16 * 16 ? largeBoardGenerationPasses : 1;
  let oriented: ReturnType<typeof orientPaths> = null;
  let ringMotifCount = 0;
  let ringEnclosedCellCount = 0;
  for (let pass = 0; pass < generationPasses && oriented === null; pass += 1) {
    const partition = partitionMaskWithMetadata(
      input.mask,
      input.params,
      rng,
      effort,
      effortLimit,
    );
    if (effort.count > effortLimit) {
      return null;
    }
    if (partition !== null) {
      oriented = orientWithPasses(
        partition.paths,
        input,
        rng,
        partition.chainPatchPathCount,
      );
      if (oriented !== null) {
        ringMotifCount = partition.ringMotifCount;
        ringEnclosedCellCount = partition.ringEnclosedCellCount;
      }
    }
  }
  if (oriented === null) {
    return null;
  }

  const vacancy = input.params.vacancy !== undefined && rectangleMaskIds.includes(input.mask.id)
    ? carveVacancies(
      oriented.arrows,
      oriented.solutionOrder,
      input.mask.width,
      input.mask.height,
      rng,
      input.params.vacancy,
      vacancyProtectedArrowIndexes(oriented),
    )
    : {
      arrows: oriented.arrows,
      solutionOrder: oriented.solutionOrder,
      deletionCount: 0,
      scoreCutoffReached: false,
    };

  const indexed = vacancy.arrows.map((arrow, pathIndex) => ({ arrow, pathIndex }));
  indexed.sort((left, right) => {
    const leftCell = left.arrow.cells[0];
    const rightCell = right.arrow.cells[0];
    return (leftCell?.y ?? 0) - (rightCell?.y ?? 0)
      || (leftCell?.x ?? 0) - (rightCell?.x ?? 0)
      || left.pathIndex - right.pathIndex;
  });
  const idsByPathIndex = new Map<number, string>();
  const arrows: ArrowDefinition[] = indexed.map(({ arrow, pathIndex }, index) => {
    const id = `a${String(index + 1).padStart(2, '0')}`;
    idsByPathIndex.set(pathIndex, id);
    return { ...arrow, id };
  });
  const solutionOrder = vacancy.solutionOrder.map((pathIndex) => {
    const id = idsByPathIndex.get(pathIndex);
    if (id === undefined) {
      throw new Error(`Missing generated arrow for path ${pathIndex}.`);
    }
    return id;
  });
  const level: LevelDefinition = {
    id: input.levelId,
    title: input.title,
    width: input.mask.width,
    height: input.mask.height,
    arrows,
  };

  return {
    level,
    solutionOrder,
    metrics: {
      ...computeMetrics(level, solutionOrder),
      ringMotifCount,
      ringEnclosedCellCount,
    },
    vacancyDeletionCount: vacancy.deletionCount,
    vacancyScoreCutoffReached: vacancy.scoreCutoffReached,
  };
}

export function generateLevelOrThrow(input: GenerationInput): GenerationResult {
  const result = generateLevel(input);
  if (result === null) {
    throw new Error(`Level generation failed for maskId="${input.mask.id}", seed=${input.seed}.`);
  }
  return result;
}

/**
 * Retries belong only at authoring time. Runtime callers persist resolvedSeed
 * and pass it to generateLevelOrThrow to reproduce the successful attempt once.
 */
export function generateWithRetries(
  input: GenerationInput,
  maxAttempts = 32,
  effortLimit = generationEffortLimit,
  acceptResult: (result: GenerationResult, attemptNumber: number) => boolean = () => true,
): RetryOutcome {
  if (!Number.isInteger(maxAttempts) || maxAttempts < 1) {
    throw new Error('maxAttempts must be a positive integer.');
  }
  if (Number.isNaN(effortLimit) || effortLimit < 0) {
    throw new Error('effortLimit must be a non-negative number.');
  }
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const resolvedSeed = deriveSeed(input.seed, attempt);
    const effort = { count: 0 };
    const result = generateLevel({ ...input, seed: resolvedSeed }, effort, effortLimit);
    if (result !== null && effort.count <= effortLimit && acceptResult(result, attempt + 1)) {
      return { result, resolvedSeed, attemptCount: attempt + 1, effort: effort.count };
    }
    // Each attempt starts from a fresh RNG derived only from its seed, so
    // abandoning an over-budget attempt cannot change the next accepted seed.
  }
  throw new Error(
    `Level generation exhausted ${maxAttempts} attempts for maskId="${input.mask.id}", seed=${input.seed}.`,
  );
}

export function levelContentHash(level: LevelDefinition): string {
  const arrows = level.arrows
    .map((arrow) => `${arrow.cells.map((cell) => `${cell.x},${cell.y}`).join(';')}|${arrow.direction}`)
    .sort()
    .join('\n');
  const serialized = `${level.width}x${level.height}\n${arrows}`;
  return fnv1a32Hex(serialized);
}
